import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { LocalStorageService } from '@vendasta/uikit';
import { BehaviorSubject, Observable, combineLatest, fromEvent as observableFromEvent, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { PartnerInfo } from '../core/partner';
import { PartnerService } from '../core/partner.service';
import { AtlasApiService, ShouldDisplayPamRequest } from '@vendasta/atlas';
import { AtlasConfigService } from '@galaxy/atlas/core';
import { PamDialogService } from '@galaxy/atlas/pam-dialog';
import { FeatureFlagService } from '@galaxy/partner';

const RecentPartnersStorageKey = 'omnisearch:recentPartners';
const MaxRecentPartners = 5;

@Component({
  templateUrl: './partner-switcher.component.html',
  styleUrls: ['./common.scss'],
  standalone: false,
})
export class PartnerSwitcherComponent implements OnInit, AfterViewInit {
  @ViewChild('searchTerm', { static: true }) searchTerm: ElementRef;
  partners$: Observable<PartnerInfo[]>;
  private recentPartners$$ = new BehaviorSubject<PartnerInfo[]>([]);
  recentPartners$: Observable<PartnerInfo[]>;

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    public dialogRef: MatDialogRef<PartnerSwitcherComponent>,
    private partnerService: PartnerService,
    private localStorageService: LocalStorageService,
    private readonly configService: AppConfigService,
    private readonly atlasApiService: AtlasApiService,
    private readonly atlasConfigService: AtlasConfigService,
    private readonly pamDialogService: PamDialogService,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    // Load recently access partners from local storage
    this.recentPartners$$.next(this.loadRecentPartners());
  }

  ngAfterViewInit(): void {
    // Get a change since check error, since we're setting up streams here? This setTimeout resolves that
    window.setTimeout(() => {
      const search$ = observableFromEvent(this.searchTerm.nativeElement, 'keyup').pipe(
        distinctUntilChanged(),
        map((_) => {
          return this.searchTerm.nativeElement.value;
        }),
      );
      const searchDebounced$ = search$.pipe(debounceTime(300));
      this.recentPartners$ = combineLatest([
        search$.pipe(startWith('')), // stream doesn't start with anything to avoid the empty partner search
        this.recentPartners$$,
      ]).pipe(
        map(([searchTerm, recentPartners]) => {
          if (!searchTerm) {
            return recentPartners;
          }
          const lowerSearchTerm = searchTerm.toLowerCase();
          return recentPartners.filter((p) => {
            return p.id.toLowerCase().includes(lowerSearchTerm) || p.name.toLowerCase().includes(lowerSearchTerm);
          });
        }),
        shareReplay(1),
      );

      this.partners$ = searchDebounced$.pipe(
        switchMap((searchTerm) => this.partnerService.search(searchTerm)),
        shareReplay(1),
      );
    }, 0);
  }

  switchPartner(partnerId: string): void {
    const upperPartnerId = partnerId.toUpperCase();
    const entitlementId$ = this.atlasConfigService.config$.pipe(map((config) => config.entitlementId))
    const featureFlag$ = this.featureFlagService.batchGetStatus(upperPartnerId, '', ['privileged_access']).pipe(
      map((resp) => resp?.['privileged_access']),
    )
    const shouldDisplayPam$ = entitlementId$.pipe(
      switchMap((entitlementId) => this.atlasApiService.shouldDisplayPam(new ShouldDisplayPamRequest({
        entitlementId: entitlementId,
        partnerId: upperPartnerId,
      }))),
      map((resp) => resp.shouldDisplayPam)
    )
    // Try to find in partners, save to recent if we can
    combineLatest([
      this.partnerId$,
      this.partners$.pipe(startWith([])), // start with empty list, since they might not be loaded yet
      this.recentPartners$,
      featureFlag$,
      shouldDisplayPam$,
    ])
      .pipe(
        take(1),
        map(([currentPartnerId, partners, recentPartners, featureFlag, shouldDisplayPam]) => {
          return [
            currentPartnerId,
            partners.find((pInfo) => pInfo.id === upperPartnerId) ||
              recentPartners.find((pInfo) => pInfo.id === upperPartnerId),
            featureFlag,
            shouldDisplayPam,
          ];
        }),
      )
      .subscribe(async ([, partnerInfo, featureFlag, shouldDisplayPam]) => {
        const partnerIdWithFallbackToInput = partnerInfo?.id || upperPartnerId;
        this.addPartnerToRecents(partnerInfo);
        if (shouldDisplayPam && featureFlag) {
          await firstValueFrom(this.pamDialogService.openDialog(partnerIdWithFallbackToInput).afterClosed())
        }
        await this.configService.load(partnerIdWithFallbackToInput); // wait for config to load before continuing
        this.dialogRef.close();
        const url = new URL(window.location.href);
        if (url.searchParams.has('_pid')) {
          url.searchParams.set('_pid', partnerIdWithFallbackToInput); // set _pid value in url to new partnerId
        }
        window.location.href = url.toString(); // ensure nothing is referencing the previous PID by reloading the page
      });
  }

  addPartnerToRecents(partnerInfo: PartnerInfo): void {
    if (!partnerInfo) {
      return;
    }

    // Get existing or default
    let recentPartners = this.loadRecentPartners();
    // Update time if found, add if not
    const found = recentPartners.findIndex((p) => p.id === partnerInfo.id);
    if (found !== -1) {
      // Update all fields of the existing partner, including lastAccessed
      recentPartners[found] = { ...partnerInfo, lastAccessed: new Date() };
    } else {
      // Set their last used date for sorting/limiting
      partnerInfo.lastAccessed = new Date();
      recentPartners.push(partnerInfo);
    }
    // Sort Data
    recentPartners = recentPartners.sort((a, b) => b.lastAccessed.valueOf() - a.lastAccessed.valueOf());
    // Limit Recents
    if (recentPartners.length > MaxRecentPartners) {
      recentPartners = recentPartners.slice(0, MaxRecentPartners);
    }
    // Update data
    this.recentPartners$$.next(recentPartners);
    // Save
    this.localStorageService.set(RecentPartnersStorageKey, JSON.stringify(recentPartners));
  }

  loadRecentPartners(): PartnerInfo[] {
    // Get existing or default
    const recentPartnersSerialized = this.localStorageService.get(RecentPartnersStorageKey);
    let recentPartners = [];
    // Deserialize as needed
    if (recentPartnersSerialized) {
      try {
        recentPartners = JSON.parse(recentPartnersSerialized);
      } catch {
        console.error('recent partners data from localStorage could not be loaded');
      }
    }
    // Create dates from strings
    recentPartners.forEach((p) => {
      p.lastAccessed = new Date(p.lastAccessed);
    });
    return recentPartners;
  }
}
